import React, { useMemo, useState } from "react";
import { ArrowRight, ChevronDown, Phone } from "lucide-react";
import { APPLY_LINK_PROPS } from "@/constants/links";

const REVENUE_BANDS = [
  { value: "lt-10k",   label: "Under $10K / month",   midpoint: 8000 },
  { value: "10-25k",   label: "$10K – $25K",          midpoint: 17500 },
  { value: "25-50k",   label: "$25K – $50K",          midpoint: 37500 },
  { value: "50-100k",  label: "$50K – $100K",         midpoint: 75000 },
  { value: "100-250k", label: "$100K – $250K",        midpoint: 175000 },
  { value: "250k-plus", label: "$250K+ / month",      midpoint: 350000 },
];

const TIB_BANDS = [
  { value: "lt-6mo",  label: "Less than 6 months", mult: 0   },
  { value: "6mo-1yr", label: "6 months – 1 year",  mult: 0.9 },
  { value: "1-2yr",   label: "1 – 2 years",        mult: 1.3 },
  { value: "2-5yr",   label: "2 – 5 years",        mult: 1.7 },
  { value: "5plus",   label: "5+ years",           mult: 2.0 },
];

const fmt = (n) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(n);

function computeQualifyAmount(revenueBand, tibBand) {
  const rev = REVENUE_BANDS.find((b) => b.value === revenueBand);
  const tib = TIB_BANDS.find((b) => b.value === tibBand);
  if (!rev || !tib) return null;

  // Newer businesses get the "let's talk first" branch
  if (tib.mult === 0) return { qualifies: false };

  const annual = rev.midpoint * 12;
  // Estimate: 10% of annual revenue × time-in-business multiplier.
  // No credit-score input — we look at business performance only.
  let est = annual * 0.10 * tib.mult;

  // Sensible floor for businesses that obviously qualify
  if (est < 25000 && rev.midpoint >= 20000) est = 25000;
  // Cap + round to nearest $5K for clean display
  est = Math.min(est, 5_000_000);
  est = Math.round(est / 5000) * 5000;

  return { qualifies: true, amount: est };
}

const Select = ({ value, onChange, options, placeholder, testId }) => (
  <div className="relative">
    <select
      data-testid={testId}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="w-full appearance-none bg-white border border-cf-line rounded-xl px-4 py-3 pr-10 text-sm text-cf-ink font-medium cursor-pointer outline-none focus:border-cf-ink focus:ring-2 focus:ring-cf-ink/10 transition"
    >
      <option value="" disabled>{placeholder}</option>
      {options.map((o) => (
        <option key={o.value} value={o.value}>{o.label}</option>
      ))}
    </select>
    <ChevronDown size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-cf-ink-soft pointer-events-none" />
  </div>
);

const PreQualify = () => {
  const [rev, setRev] = useState("");
  const [tib, setTib] = useState("");

  const result = useMemo(() => {
    if (!rev || !tib) return null;
    return computeQualifyAmount(rev, tib);
  }, [rev, tib]);

  return (
    <div
      data-testid="pre-qualify-widget"
      className="bg-white rounded-3xl border border-cf-line shadow-[0_30px_70px_-30px_rgba(15,37,64,0.25)] p-6 md:p-8"
    >
      <div className="flex items-center gap-2 mb-1">
        <span className="cf-overline" style={{ letterSpacing: "0.18em" }}>Pre-qualify in 20 seconds</span>
      </div>
      <h3 className="font-serif text-2xl md:text-3xl text-cf-ink leading-tight tracking-tight">
        How much can your business qualify for?
      </h3>
      <p className="text-cf-slate text-sm mt-2 leading-relaxed">
        No credit pull. No credit score required. Just an honest estimate from a direct lender.
      </p>

      <div className="grid sm:grid-cols-2 gap-3 mt-6">
        <div>
          <label className="block text-[0.7rem] font-semibold text-cf-ink-soft mb-2 uppercase tracking-wider">Monthly revenue</label>
          <Select value={rev} onChange={setRev} options={REVENUE_BANDS} placeholder="Select revenue" testId="pq-revenue" />
        </div>
        <div>
          <label className="block text-[0.7rem] font-semibold text-cf-ink-soft mb-2 uppercase tracking-wider">Time in business</label>
          <Select value={tib} onChange={setTib} options={TIB_BANDS} placeholder="Select duration" testId="pq-tib" />
        </div>
      </div>

      {/* Result */}
      {result && (
        <div
          data-testid="pq-result"
          className={`mt-6 rounded-2xl p-5 md:p-6 border ${
            result.qualifies
              ? "bg-cf-mint border-cf-gold/30"
              : "bg-cf-paper border-cf-line"
          }`}
        >
          {result.qualifies ? (
            <>
              <div className="text-xs uppercase tracking-[0.18em] text-cf-ink-soft font-semibold">You likely pre-qualify for up to</div>
              <div className="font-serif text-4xl md:text-5xl text-cf-ink tracking-tight mt-1" data-testid="pq-amount">
                {fmt(result.amount)}
              </div>
              <p className="text-cf-ink-soft text-sm mt-3 leading-relaxed">
                Estimate based on the info you provided. Final terms depend on a full review of your business — but you&apos;re a great fit for a same-day call.
              </p>
              <a {...APPLY_LINK_PROPS} className="cf-btn-gold mt-5 inline-flex" data-testid="pq-apply-btn">
                Apply for this amount <ArrowRight size={16} strokeWidth={2} />
              </a>
            </>
          ) : (
            <>
              <div className="text-xs uppercase tracking-[0.18em] text-cf-ink-soft font-semibold">Let&apos;s talk first</div>
              <div className="font-serif text-2xl md:text-3xl text-cf-ink tracking-tight mt-2">
                You may still qualify — let&apos;s figure it out together.
              </div>
              <p className="text-cf-ink-soft text-sm mt-3 leading-relaxed">
                Newer businesses can still qualify for the right program. Call an advisor — most files we couldn&apos;t place online qualify after a 5-minute conversation.
              </p>
              <div className="flex flex-wrap gap-3 mt-5">
                <a href="tel:+17377757077" className="cf-btn-primary inline-flex">
                  <Phone size={15} strokeWidth={1.8} /> Talk to an advisor
                </a>
                <a {...APPLY_LINK_PROPS} className="cf-btn-ghost-light inline-flex" data-testid="pq-apply-anyway-btn">
                  Apply anyway <ArrowRight size={16} strokeWidth={2} />
                </a>
              </div>
            </>
          )}
        </div>
      )}

      {!result && (
        <div className="mt-6 text-cf-slate text-xs">
          Pick both to see your estimate instantly. <span className="text-cf-gold font-semibold">No credit pull, no credit score required.</span>
        </div>
      )}
    </div>
  );
};

export default PreQualify;
